/* CART DRAWER  */

const CART_DRAWER_ID = "cart_drawer";
const CART_DRAWER_RECOMMENDATION_INTENTS = ["complementary", "related"];
const CART_DRAWER_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';


function cartDrawerFocusable(root) {
  return Array.from(root.querySelectorAll(CART_DRAWER_FOCUSABLE)).filter(
    (element) => element.getClientRects().length > 0,
  );
}


function cartDrawerTabOrder(wrapper) {
  const ordered = [];
  const seen = new Set();

  const add = (root) => {
    if (!root) return;

    cartDrawerFocusable(root).forEach((element) => {
      if (seen.has(element) || element.closest("[inert]")) return;

      seen.add(element);
      ordered.push(element);
    });
  };

  const tools = wrapper.querySelector("cart-drawer-tools");
  const hasTools = tools && !tools.hasAttribute("hidden");

  add(wrapper.querySelector(".cart_drawer_header"));
  add(wrapper.querySelector(".cart_drawer_content_wrapper"));
  add(wrapper.querySelector(".cart_drawer_recommendations"));

  if (hasTools) {
    add(tools.querySelector(".cart_drawer_tools_rail"));
    add(tools.querySelector("[data-tool-panel]:not([hidden])"));
  }

  add(wrapper.querySelector(".cart_drawer_footer"));
  add(wrapper);

  return ordered;
}


class CartDrawerTools extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-tool-tab]"));

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.toggle(tab.dataset.toolTab));
    });

    this.querySelectorAll("[data-tool-close]").forEach((button) => {
      button.addEventListener("click", () => this.close());
    });

   
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.openPanelName) return;

      event.stopPropagation();
      this.close();
    });
  }

  get openPanelName() {
    return this.dataset.openPanel || "";
  }

  get panels() {
    return Array.from(this.querySelectorAll("[data-tool-panel]"));
  }

  panelFor(name) {
    return this.querySelector(`[data-tool-panel="${name}"]`);
  }

  tabFor(name) {
    return this.querySelector(`[data-tool-tab="${name}"]`);
  }

  toggle(name) {
    if (this.openPanelName === name) {
      this.close();
    } else {
      this.open(name);
    }
  }

  open(name) {
    this.tabs.forEach((tab) => {
      tab.setAttribute("aria-expanded", tab.dataset.toolTab === name ? "true" : "false");
    });

    this.panels.forEach((panel) => {
      const isOpen = panel.dataset.toolPanel === name;

      panel.toggleAttribute("hidden", !isOpen);
      panel.toggleAttribute("inert", !isOpen);
    });

    this.dataset.openPanel = name;
    this.classList.add("is_open");
    this.focusPanel(name);
  }

  close({ returnFocus = true } = {}) {
    const name = this.openPanelName;

    this.tabs.forEach((tab) => tab.setAttribute("aria-expanded", "false"));
    this.panels.forEach((panel) => {
      panel.setAttribute("hidden", "");
      panel.setAttribute("inert", "");
    });

    delete this.dataset.openPanel;
    this.classList.remove("is_open");

    if (returnFocus && name) this.tabFor(name)?.focus();
  }

  focusPanel(name) {
    const panel = this.panelFor(name);
    if (!panel) return;

    cartDrawerFocusable(panel)[0]?.focus();
  }

  
  refresh(doc) {
    const source = doc.querySelector("cart-drawer-tools");
    if (!source) return;

    const incoming = source.querySelectorAll("[data-tool-refresh]");

    this.querySelectorAll("[data-tool-refresh]").forEach((target, index) => {
      const fresh = incoming[index];
      if (!fresh) return;

     
      const activeElement = document.activeElement;
      if (target.contains(activeElement) && activeElement.matches("input, textarea")) return;

      const hadFocus = target.contains(activeElement);
      target.replaceWith(fresh);

      if (hadFocus) this.focusPanel(this.openPanelName);
    });
  }
}

if (!customElements.get("cart-drawer-tools")) {
  customElements.define("cart-drawer-tools", CartDrawerTools);
}


class CartDrawerRecommendations {
  constructor(container) {
    this.container = container;
    this.loading = false;
  }

  get productId() {
    return this.container.dataset.productId;
  }

  set productId(value) {
    if (this.productId === value) return;

    this.container.dataset.productId = value || "";
    delete this.container.dataset.loadedId;
  }

  async load() {
    const productId = this.productId;

    if (!productId || this.loading || this.container.dataset.loadedId === productId) return;
    this.loading = true;

    try {
      /* Merchants have to set complementary products up, so related is the backstop */
      for (const intent of CART_DRAWER_RECOMMENDATION_INTENTS) {
        const params = new URLSearchParams({
          section_id: "cart-drawer-recommendations",
          product_id: productId,
          limit: "4",
          intent,
        });

        const response = await fetch(`${this.container.dataset.url}?${params}`);
        if (!response.ok) continue;

        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const list = doc.querySelector("[data-cart-recommendations-list]");

        if (!list?.querySelector("li")) continue;

        this.container.replaceChildren(list);
        this.container.dataset.loadedId = productId;
        return;
      }

      /* Nothing to suggest for this cart */
      this.container.replaceChildren();
      this.container.dataset.loadedId = productId;
    } catch (error) {
      console.error("Cart drawer recommendations failed to load:", error);
    } finally {
      this.loading = false;
    }
  }
}

function initCartDrawer() {
  const wrapper = document.getElementById(CART_DRAWER_ID);
  if (!wrapper || wrapper.dataset.cartDrawerInitialized) return;
  wrapper.dataset.cartDrawerInitialized = "true";

  const tools = wrapper.querySelector("cart-drawer-tools");
  const recommendationsEl = wrapper.querySelector("[data-cart-recommendations]");
  const recommendations = recommendationsEl ? new CartDrawerRecommendations(recommendationsEl) : null;

 
  wrapper.addEventListener("click", (event) => {
    if (!event.target.closest("[data-cart-drawer-close]")) return;

    closeDrawer(wrapper);
    RemoveScrollLock();
  });

  
  wrapper.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;

    const focusable = cartDrawerTabOrder(wrapper);
    if (!focusable.length) return;

    const active = document.activeElement;
    const index = focusable.indexOf(active);

    event.preventDefault();

    if (index === -1) {
      focusable[event.shiftKey ? focusable.length - 1 : 0].focus();
    } else {
      const next = (index + (event.shiftKey ? -1 : 1) + focusable.length) % focusable.length;
      focusable[next].focus();
    }

    event.stopImmediatePropagation();
  });

  new MutationObserver(() => {
    if (wrapper.classList.contains("active")) {
      recommendations?.load();
    } else {
      tools?.close({ returnFocus: false });
    }
  }).observe(wrapper, { attributes: true, attributeFilter: ["class"] });

 
  document.addEventListener("cart-updated", (event) => {
    const html = event.detail?.sections?.["cart-drawer"];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const isEmpty = !doc.querySelector(".cart_drawer_item_block");

    const freshFooter = doc.querySelector(".cart_drawer_footer");
    const currentFooter = wrapper.querySelector(".cart_drawer_footer");
    if (freshFooter && currentFooter) currentFooter.replaceWith(freshFooter);

    if (tools) {
      tools.toggleAttribute("hidden", isEmpty);
      if (isEmpty) tools.close({ returnFocus: false });
      tools.refresh(doc);
    }

    if (recommendations) {
      recommendationsEl.toggleAttribute("hidden", isEmpty);
      recommendations.productId = doc.querySelector("[data-cart-recommendations]")?.dataset.productId;

      if (!isEmpty && wrapper.classList.contains("active")) recommendations.load();
    }
  });
}

document.addEventListener("DOMContentLoaded", initCartDrawer);
